import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, CircularProgress, Alert, alpha, useTheme, Paper,
} from '@mui/material';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BrushIcon from '@mui/icons-material/Brush';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { formatDateLong } from '../../utils/dateUtils';
import LeaveSection from './LeaveSection';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

const STATUS_META = {
  'Waiting for Artwork': { color: '#e65100', label: 'Waiting for Artwork' },
  'In Design':           { color: '#1976d2', label: 'In Design' },
  'Proofing':            { color: '#7b1fa2', label: 'Proofing' },
  'Proof Pending':       { color: '#7b1fa2', label: 'Proof Pending' },
  'Approved':            { color: '#2e7d32', label: 'Approved' },
  'Uploaded':            { color: '#00897b', label: 'Uploaded' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return <Chip size="small" label={status || 'Unknown'} />;
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{ bgcolor: meta.color + '20', color: meta.color, fontWeight: 500 }}
    />
  );
}

// ─── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, icon, color }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%', borderRadius: 2,
        border: `1px solid ${alpha(color || theme.palette.primary.main, 0.18)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 40px 0 rgba(0,0,0,0.10)' },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: color || 'text.primary' }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {sub}
              </Typography>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 52, height: 52, borderRadius: 1.5, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(color || theme.palette.primary.main, 0.1),
                color: color || theme.palette.primary.main,
                '& svg': { fontSize: '1.6rem' },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Design Queue Table ───────────────────────────────────────────────────────

const TAB_FILTERS = [
  { label: 'All',                statuses: null },
  { label: 'Waiting for Art',    statuses: ['Waiting for Artwork'] },
  { label: 'In Design',          statuses: ['In Design'] },
  { label: 'Proofing',           statuses: ['Proofing', 'Proof Pending'] },
  { label: 'Approved',           statuses: ['Approved', 'Uploaded'] },
];

function DesignQueueTable({ orders }) {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  const filtered = tab === 0
    ? orders
    : orders.filter(o => TAB_FILTERS[tab].statuses.includes(o.status));

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
      }}
    >
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DesignServicesIcon sx={{ color: 'primary.main' }} /> Design Queue
        </Typography>
        <Button component={Link} to="/app/orders" size="small" variant="outlined">
          View All Orders
        </Button>
      </Box>

      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: '0.8rem' } }}
        >
          {TAB_FILTERS.map((f, i) => (
            <Tab
              key={f.label}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {f.label}
                  <Chip
                    size="small"
                    label={
                      i === 0
                        ? orders.length
                        : orders.filter(o => f.statuses.includes(o.status)).length
                    }
                    sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {filtered.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Business / School</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sales Person</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(order => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>ORD-{order.id}</Typography>
                  </TableCell>
                  <TableCell>{order.businessName}</TableCell>
                  <TableCell>
                    <Chip size="small" label={order.orderType || 'ad'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={order.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {order.salesPersonName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateLong(order.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography color="text.secondary">
            {tab === 0 ? 'Design queue is clear.' : 'No orders in this stage.'}
          </Typography>
        </Box>
      )}
    </Card>
  );
}

// ─── DesignerDashboard ────────────────────────────────────────────────────────

export default function DesignerDashboard() {
  const { token, user } = useAuth();
  const theme = useTheme();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const monthLabel = new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${config.apiUrl}/api/dashboard/designer-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) setData(json.data);
      else setError('Failed to load dashboard data');
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 4, pb: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            {greeting()}, {firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your design queue for {monthLabel}
          </Typography>
        </Box>
        <Button variant="contained" color="primary" startIcon={<CalendarTodayIcon />} size="small">
          {monthLabel}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── Row 1: Pipeline Status Cards ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Total in Queue"
            value={data?.totalInQueue ?? 0}
            sub="Active design orders"
            color={theme.palette.primary.main}
            icon={<AssignmentIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Waiting for Artwork"
            value={data?.waitingForArtwork ?? 0}
            sub="Awaiting client files"
            color="#e65100"
            icon={<BrushIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="In Design / Proofing"
            value={(data?.inDesign ?? 0) + (data?.awaitingProof ?? 0)}
            sub={`${data?.inDesign ?? 0} designing · ${data?.awaitingProof ?? 0} proofing`}
            color={theme.palette.info.main}
            icon={<FindInPageIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Approved / Uploaded"
            value={data?.approvedReady ?? 0}
            sub="Ready to publish"
            color={theme.palette.success.main}
            icon={<CheckCircleIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Row 2: Pipeline Progress Bar ── */}
      {data?.totalInQueue > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 4, p: 2.5, borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Pipeline Distribution
          </Typography>
          <Box sx={{ display: 'flex', height: 12, borderRadius: 2, overflow: 'hidden', mb: 1.5 }}>
            {[
              { val: data.waitingForArtwork, color: '#e65100', label: 'Waiting' },
              { val: data.inDesign,          color: '#1976d2', label: 'In Design' },
              { val: data.awaitingProof,     color: '#7b1fa2', label: 'Proofing' },
              { val: data.approvedReady,     color: '#2e7d32', label: 'Approved' },
            ].map(({ val, color, label }) => {
              const pct = data.totalInQueue > 0 ? (val / data.totalInQueue) * 100 : 0;
              return pct > 0 ? (
                <Box
                  key={label}
                  sx={{ width: `${pct}%`, bgcolor: color, transition: 'width 0.5s ease' }}
                  title={`${label}: ${val}`}
                />
              ) : null;
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {[
              { val: data.waitingForArtwork, color: '#e65100', label: 'Waiting for Artwork' },
              { val: data.inDesign,          color: '#1976d2', label: 'In Design' },
              { val: data.awaitingProof,     color: '#7b1fa2', label: 'Proofing' },
              { val: data.approvedReady,     color: '#2e7d32', label: 'Approved / Uploaded' },
            ].map(({ val, color, label }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                <Typography variant="caption" color="text.secondary">{label}: {val}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* ── Row 3: Design Queue Table ── */}
      <Box sx={{ mb: 3 }}>
        <DesignQueueTable orders={data?.recentOrders || []} />
      </Box>

      {/* ── Leave ── */}
      <LeaveSection />
    </Box>
  );
}
